import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Layout from '../../components/Layout';

const InventoryReportPage = () => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [filters, setFilters] = useState({
    search: '',
    storeId: '',
    warehouseId: ''
  });
  const [stores, setStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Fetch stores and warehouses for filter dropdowns
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const [storesRes, warehousesRes] = await Promise.all([
          api.get('stores', { queryParams: { limit: 1000 } }),
          api.get('warehouses', { queryParams: { limit: 1000 } })
        ]);
        setStores(storesRes?.data || storesRes || []);
        setWarehouses(warehousesRes?.data || warehousesRes || []);
      } catch (err) {
        console.error('Failed to load locations:', err);
      }
    };
    fetchLocations();
  }, [api]);

  // Fetch consolidated inventory report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;

      const response = await api.get('inventory-log/report/consolidated', {
        queryParams: params
      });
      setReportData(response?.data || response);
    } catch (err) {
      setError(err?.message || 'Failed to load inventory report');
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  // Fetch report on mount and when filters change
  useEffect(() => {
    fetchReport();
  }, [filters, fetchReport]);

  const toggleProductExpanded = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(String(productId))) {
      newExpanded.delete(String(productId));
    } else {
      newExpanded.add(String(productId));
    }
    setExpandedProducts(newExpanded);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const exportToCsv = () => {
    if (!reportData?.products || reportData.products.length === 0) {
      alert('No data to export');
      return;
    }

    let csv = 'Product Name,SKU,Price,Total Stock,Master Stock\n';
    reportData.products.forEach(product => {
      csv += `"${product.name}","${product.sku || ''}",${product.price},${product.totalStock},${product.masterStock}\n`;
      product.locations?.forEach(location => {
        csv += `,"",,"${location.name} (${location.type})",${location.totalQuantity}\n`;
        location.batches?.forEach(batch => {
          const expiryDate = batch.expiryDate ? new Date(batch.expiryDate).toLocaleDateString() : 'N/A';
          csv += `"","${batch.batchNumber || ''}",,"Batch",${batch.quantity},"Expiry: ${expiryDate}"\n`;
        });
      });
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading && !reportData) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Inventory Report</h1>
          <p className="text-gray-600">Complete inventory status across all stores and warehouses</p>
        </div>

        {error && <ErrorMessage message={error} />}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Product</label>
              <input
                type="text"
                placeholder="Search by name or SKU"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Store</label>
              <select
                value={filters.storeId}
                onChange={(e) => handleFilterChange('storeId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Stores</option>
                {stores.map(store => (
                  <option key={store._id || store.id} value={store._id || store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Warehouse</label>
              <select
                value={filters.warehouseId}
                onChange={(e) => handleFilterChange('warehouseId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Warehouses</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse._id || warehouse.id} value={warehouse._id || warehouse.id}>
                    {warehouse.name || warehouse.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {reportData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600 text-sm font-medium">Total Products</p>
              <p className="text-2xl font-bold text-indigo-600 mt-2">{reportData.summary.totalProducts}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600 text-sm font-medium">Total Stock Units</p>
              <p className="text-2xl font-bold text-green-600 mt-2">{reportData.summary.totalStockUnits}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600 text-sm font-medium">Store Locations</p>
              <p className="text-2xl font-bold text-blue-600 mt-2">{reportData.summary.totalStoreLocations}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600 text-sm font-medium">Warehouse Locations</p>
              <p className="text-2xl font-bold text-orange-600 mt-2">{reportData.summary.totalWarehouseLocations}</p>
            </div>
          </div>
        )}

        {/* Export Button */}
        <div className="mb-6">
          <button
            onClick={exportToCsv}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition"
          >
            Export to CSV
          </button>
        </div>

        {/* Products List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading && !reportData?.products ? (
            <div className="p-6 text-center">
              <LoadingSpinner />
            </div>
          ) : reportData?.products && reportData.products.length > 0 ? (
            <div className="overflow-x-auto">
              {reportData.products.map((product) => (
                <div
                  key={String(product._id)}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  {/* Product Header */}
                  <div
                    onClick={() => toggleProductExpanded(product._id)}
                    className="p-6 hover:bg-gray-50 cursor-pointer transition flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <button className="text-indigo-600">
                          {expandedProducts.has(String(product._id)) ? '▼' : '▶'}
                        </button>
                        <div>
                          <h3 className="font-semibold text-gray-900">{product.name}</h3>
                          {product.sku && (
                            <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right mr-4">
                      <p className="text-sm text-gray-600">Price: ₹{product.price}</p>
                      <p className="text-sm text-gray-600">Master Stock: {product.masterStock}</p>
                    </div>
                    <div className="text-right min-w-[150px]">
                      <p className="text-2xl font-bold text-indigo-600">{product.totalStock}</p>
                      <p className="text-xs text-gray-500">Total Stock</p>
                    </div>
                  </div>

                  {/* Location Details (Expanded) */}
                  {expandedProducts.has(String(product._id)) && (
                    <div className="bg-gray-50 border-t border-gray-200 p-6">
                      {product.locations && product.locations.length > 0 ? (
                        <div className="space-y-6">
                          {product.locations.map((location, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{location.name}</h4>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {location.type === 'store' ? '🏪 Store' : '📦 Warehouse'}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-green-600">{location.totalQuantity}</p>
                                  <p className="text-xs text-gray-500">Units</p>
                                </div>
                              </div>

                              {/* Batches for this location */}
                              {location.batches && location.batches.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Batches:</p>
                                  <div className="space-y-2">
                                    {location.batches.map((batch, batchIdx) => (
                                      <div key={batchIdx} className="text-sm bg-gray-50 p-2 rounded flex justify-between">
                                        <div>
                                          <span className="font-mono text-gray-700">
                                            {batch.batchNumber || 'N/A'}
                                          </span>
                                        </div>
                                        <div className="text-right">
                                          <span className="font-semibold text-gray-900 mr-4">
                                            {batch.quantity} units
                                          </span>
                                          {batch.expiryDate && (
                                            <span className={`text-xs ${
                                              new Date(batch.expiryDate) < new Date()
                                                ? 'text-red-600 font-semibold'
                                                : 'text-gray-500'
                                            }`}>
                                              Exp: {new Date(batch.expiryDate).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">No batch locations recorded</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No products with inventory found matching your filters
            </div>
          )}
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default InventoryReportPage;
